// ── On-chain verified goal boosts ────────────────────────────────
//
// A goal-boost event is self-signed and proves nothing: anyone can publish
// one without paying, and the honest flow even publishes the event BEFORE
// sending BCH (so an abandoned payment leaves an event behind). This hook
// takes the raw goal-boost events, confirms each one's txid actually paid
// the boost wallet on the Bitcoin Cash chain, and returns ONLY the boosts
// whose payment is verified.
//
// Grace window: a genuinely fresh payment can take a minute or two to show
// up on the explorers, so a boost that's still 'not-found'/'unavailable' is
// shown provisionally for GRACE_WINDOW_SECS after it was published. After
// that, an unverifiable boost is dropped — this is what closes the
// "free boost" hole while never punishing a real, just-paid boost.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  parseGoalBoostData, verifyBoostPayment,
  type GoalBoostData, type BoostVerifyResult,
} from '../lib/boost';

const GRACE_WINDOW_SECS = 20 * 60; // 20 min provisional display for fresh boosts

// Module-level cache so we don't re-hit the explorers for the same txid.
// txid → { result, checkedAt }
const _verifyCache = new Map<string, { result: BoostVerifyResult; checkedAt: number }>();
// Re-check a still-pending (not-found/unavailable) txid at most this often.
const RECHECK_MS = 60_000;

interface RawBoost {
  event: { id: string; pubkey: string; created_at: number };
  data: GoalBoostData;
}

/**
 * Given raw goal-boost events, return the map creatorPubkey → verified boost.
 * Only boosts with a confirmed on-chain payment (or a still-fresh one inside
 * its grace window) are included.
 */
export function useVerifiedGoalBoosts(goalBoostEvents: { id: string; pubkey: string; created_at: number; content: string; tags: string[][] }[]): {
  verified: Map<string, GoalBoostData>;
  loading: boolean;
} {
  // Reduce to the active boosts we might trust: one highest-bid boost per
  // creator that hasn't expired. (Verification is per-txid below.)
  const candidates = useMemo<RawBoost[]>(() => {
    const now = Math.floor(Date.now() / 1000);
    const best = new Map<string, RawBoost>();
    for (const ev of goalBoostEvents) {
      const data = parseGoalBoostData(ev);
      if (!data) continue;
      if (data.expiresAt <= now) continue;
      const existing = best.get(data.creatorPubkey);
      if (!existing ||
          data.priceSats > existing.data.priceSats ||
          (data.priceSats === existing.data.priceSats && data.expiresAt > existing.data.expiresAt)) {
        best.set(data.creatorPubkey, { event: ev, data });
      }
    }
    return [...best.values()];
  }, [goalBoostEvents]);

  const [verifiedTick, setVerifiedTick] = useState(0);
  const [loading, setLoading] = useState(true);
  const runningRef = useRef(false);

  // A stable signature of the candidate txids so the effect only re-runs
  // when the set of boosts to check actually changes.
  const sig = useMemo(
    () => candidates.map(c => `${c.data.creatorPubkey}:${c.data.txid || '-'}:${c.data.priceSats}`).sort().join('|'),
    [candidates],
  );

  useEffect(() => {
    let cancelled = false;
    if (runningRef.current) return;

    const run = async () => {
      runningRef.current = true;
      setLoading(true);
      const now = Date.now();

      const toCheck = candidates.filter(c => {
        const key = `${c.data.txid || ''}:${c.data.priceSats}`;
        const cached = _verifyCache.get(key);
        if (!cached) return true;
        // Re-check pending results periodically; 'paid'/'unpaid' are final.
        if ((cached.result === 'not-found' || cached.result === 'unavailable') &&
            now - cached.checkedAt > RECHECK_MS) return true;
        return false;
      });

      // Verify sequentially-ish with small concurrency to be gentle on the
      // explorers (they're the same ones NIP-05 audit uses).
      const CONCURRENCY = 3;
      for (let i = 0; i < toCheck.length; i += CONCURRENCY) {
        if (cancelled) break;
        const slice = toCheck.slice(i, i + CONCURRENCY);
        await Promise.all(slice.map(async c => {
          const key = `${c.data.txid || ''}:${c.data.priceSats}`;
          const result = await verifyBoostPayment(c.data.txid, c.data.priceSats);
          _verifyCache.set(key, { result, checkedAt: Date.now() });
        }));
        if (!cancelled) setVerifiedTick(t => t + 1);
      }

      if (!cancelled) { setLoading(false); setVerifiedTick(t => t + 1); }
      runningRef.current = false;
    };

    run();
    return () => { cancelled = true; runningRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  const verified = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    const map = new Map<string, GoalBoostData>();
    for (const c of candidates) {
      const key = `${c.data.txid || ''}:${c.data.priceSats}`;
      const cached = _verifyCache.get(key);
      const verdict = cached?.result;

      if (verdict === 'paid') {
        map.set(c.data.creatorPubkey, c.data);
        continue;
      }
      // 'unpaid' is a hard rejection — never show it.
      if (verdict === 'unpaid') continue;

      // Still pending (not-found/unavailable) or not yet checked: show it
      // provisionally only while inside the grace window from publish time.
      const ageSecs = now - c.event.created_at;
      if (ageSecs <= GRACE_WINDOW_SECS) {
        map.set(c.data.creatorPubkey, c.data);
      }
    }
    return map;
    // verifiedTick forces recomputation as verification results arrive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates, verifiedTick]);

  return { verified, loading };
}
